import { InputGroup, GenericCombobox, PageSection, EU_COUNTRIES } from '@/common'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { orderBy } from 'es-toolkit'
import { DeclarantFormData } from './DeclarantForm.schema'
import { DeclarantType } from '@/features/cbam-reporting/hooks/declarants'
import DeclarantTypeSelector from '@/shared/components/DeclarantTypeSelector'

type DeclarantFormProps = {
  data: DeclarantFormData
  onChange: (data: DeclarantFormData) => void
  isEditMode?: boolean
}

type FormFieldHandlers = {
  onInputChange: (
    evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => void
  onIdentificationNumberChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onCountryChange: (value: string) => void
  onTypeChange: (value: DeclarantType) => void
}

type CountryOption = {
  label: string
  value: string
}

function DeclarantFormFields({
  data,
  handlers,
  isEditMode,
  countryOptions,
  selectedCountry,
  countryName
}: {
  data: DeclarantFormData
  handlers: FormFieldHandlers
  isEditMode: boolean
  countryOptions?: CountryOption[]
  selectedCountry?: CountryOption | null
  countryName?: string
}) {
  const { t } = useTranslation()
  const { onInputChange, onIdentificationNumberChange, onCountryChange, onTypeChange } = handlers

  return (
    <div className="space-y-6">
      <PageSection title={t('declarants:form.sections.basicInfo')}>
        <div className="grid grid-cols-2 gap-4">
          <InputGroup
            id="name"
            label={t('declarants:form.fields.name')}
            value={data.name}
            onChange={onInputChange}
            required
          />
          <InputGroup
            id="identificationNumber"
            label={t('declarants:form.fields.identificationNumber')}
            value={data.identificationNumber}
            onChange={onIdentificationNumberChange}
            required
          />
        </div>
        {isEditMode ? (
          <DeclarantTypeSelector value={data.type} disabled={true} />
        ) : (
          <DeclarantTypeSelector value={data.type} onChange={onTypeChange} />
        )}
      </PageSection>

      <PageSection title={t('declarants:form.sections.address')}>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3">
            <InputGroup
              id="street"
              label={t('declarants:form.fields.street')}
              value={data.street}
              onChange={onInputChange}
            />
          </div>
          <div className="col-span-3">
            <InputGroup
              id="streetNumber"
              label={t('declarants:form.fields.streetNumber')}
              value={data.streetNumber}
              onChange={onInputChange}
            />
          </div>
          <div className="col-span-6">
            <InputGroup
              id="street2"
              label={t('declarants:form.fields.street2')}
              value={data.street2}
              onChange={onInputChange}
            />
          </div>
          <div className="col-span-2">
            <InputGroup
              id="postCode"
              label={t('declarants:form.fields.postCode')}
              value={data.postCode}
              onChange={onInputChange}
            />
          </div>
          <div className="col-span-2">
            <InputGroup
              id="city"
              label={t('declarants:form.fields.city')}
              value={data.city}
              onChange={onInputChange}
              required={isEditMode}
            />
          </div>
          <div className="col-span-2">
            <InputGroup
              id="poBox"
              label={t('declarants:form.fields.poBox')}
              value={data.poBox}
              onChange={onInputChange}
            />
          </div>
          <div className="col-span-3">
            <InputGroup
              id="subDivision"
              label={t('declarants:form.fields.subDivision')}
              value={data.subDivision}
              onChange={onInputChange}
            />
          </div>
          <div className="col-span-3">
            {isEditMode ? (
              <InputGroup
                id="country"
                label={t('declarants:form.fields.country')}
                value={countryName || ''}
                disabled
              />
            ) : (
              <GenericCombobox
                required
                label={t('declarants:form.fields.country')}
                value={selectedCountry || null}
                onChange={evt => onCountryChange(evt?.value || '')}
                items={countryOptions || []}
              />
            )}
          </div>
        </div>
      </PageSection>
    </div>
  )
}

export default function DeclarantForm({ data, onChange, isEditMode = false }: DeclarantFormProps) {
  const { t } = useTranslation()

  const onInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...data, [evt.target.id]: evt.target.value })
    },
    [data, onChange]
  )

  const onIdentificationNumberChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...data, identificationNumber: evt.target.value.trim() })
    },
    [data, onChange]
  )

  const onCountryChange = useCallback(
    (value: string) => {
      onChange({ ...data, country: value })
    },
    [data, onChange]
  )

  const onTypeChange = useCallback(
    (value: DeclarantType) => {
      onChange({ ...data, type: value })
    },
    [data, onChange]
  )

  const handlers: FormFieldHandlers = {
    onInputChange,
    onIdentificationNumberChange,
    onCountryChange,
    onTypeChange
  }

  const countryOptions = useMemo(
    () =>
      orderBy(
        EU_COUNTRIES.map(value => ({
          label: t(`countriesByIsoAlpha2.${value}`),
          value
        })),
        [option => option.label.toLowerCase().normalize('NFKD')],
        ['asc']
      ),
    [t]
  )

  const selectedCountry = useMemo(
    () => countryOptions.find(option => option.value === data.country) || null,
    [countryOptions, data.country]
  )

  const countryName = t(`countriesByIsoAlpha2.${data.country}`)

  return (
    <DeclarantFormFields
      data={data}
      handlers={handlers}
      isEditMode={isEditMode}
      countryOptions={countryOptions}
      selectedCountry={selectedCountry}
      countryName={countryName}
    />
  )
}
